import { Link } from '@ksyos/router';
import bind from 'bind-decorator';
import { ManagerAuthorization } from '@common/enums';
import { assertHasAuthorization } from '@frontend/utils';
import type { IInsurerServiceDeclarationRule } from '../../interfaces/insurerServiceDeclarationRule';
import { ValidationErrorCode } from '../../../../common/enums/validationErrorCode';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import {
    AdminTable,
    DangerAlertWhenErrorCode,
    DeleteButton,
    EditButton,
    Header,
    LinkButton,
    Loading,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from '../../elements/common';
import { Component } from '../../elements/Component';
import { ListPager } from '../../elements/ListPager';
import { NullableFormattedDate } from '../../elements/NullableFormattedDate';

interface IInsurerServiceDeclarationRuleListProps {
    insurerId: string;
}

interface IInsurerServiceDeclarationRuleListState {
    authorization: {
        canAdd: boolean;
    };
    errorCode?: string;
    insurerServiceDeclarationRules: IInsurerServiceDeclarationRule[];
    isSearching: boolean;
    page: number;
    total: number;
}

export class InsurerServiceDeclarationRuleList extends Component<
    IInsurerServiceDeclarationRuleListProps,
    IInsurerServiceDeclarationRuleListState
> {
    constructor(props: IInsurerServiceDeclarationRuleListProps, context?: any) {
        super(props, context);

        assertHasAuthorization(ManagerAuthorization.insurersAddEdit);

        this.state = {
            insurerServiceDeclarationRules: [],
            isSearching: true,
            total: 0,
            authorization: {
                canAdd: false,
            },
            page: 1,
        };
    }

    public componentDidMount() {
        this.search();
    }

    public render() {
        let actionButtons: JSX.Element[] | undefined;
        if (this.state.authorization.canAdd) {
            actionButtons = [
                <LinkButton
                    icon="add"
                    to={`/insurerServiceDeclarationRules/add?insurerId=${this.props.insurerId}`}
                >
                    {translateKey('common.addItem', translateKey('serviceDeclarationRules.serviceDeclarationRule'))}
                </LinkButton>,
            ];
        }

        return (
            <>
                <Header
                    actionButtons={actionButtons}
                    content={translateKey('serviceDeclarationRules.serviceDeclarationRules')}
                />
                <DangerAlertWhenErrorCode errorCode={this.state.errorCode} />
                <Loading
                    item={this.state.insurerServiceDeclarationRules}
                    loaded={(insurerServiceDeclarationRules) => (
                        <>
                            <AdminTable>
                                <TableHead>
                                    <TableRow>
                                        <TableHeader>{translateKey('common.name')}</TableHeader>
                                        <TableHeader>{translateKey('insurers.exportUntilDate')}</TableHeader>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {insurerServiceDeclarationRules.map((insurerServiceDeclarationRule) => (
                                        <TableRow key={insurerServiceDeclarationRule.id}>
                                            <TableData>
                                                <Link to={`/services/${insurerServiceDeclarationRule.service.id}`}>
                                                    {insurerServiceDeclarationRule.service.name}{' '}
                                                </Link>
                                            </TableData>
                                            <TableData>
                                                <NullableFormattedDate
                                                    value={insurerServiceDeclarationRule.exportUntilDate}
                                                />
                                            </TableData>
                                            {insurerServiceDeclarationRule.authorization.canUpdate && (
                                                <TableData align="right">
                                                    <EditButton
                                                        to={`/insurerServiceDeclarationRules/${insurerServiceDeclarationRule.id}/edit?insurerId=${this.props.insurerId}&returnPage=${this.state.page}`}
                                                    />
                                                    {insurerServiceDeclarationRule.authorization.canDelete && (
                                                        <DeleteButton
                                                            onClick={() =>
                                                                this.remove(insurerServiceDeclarationRule.id)
                                                            }
                                                        />
                                                    )}
                                                </TableData>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </AdminTable>
                            <ListPager
                                isDisabled={this.state.isSearching}
                                onChangePage={this.handleNavigation}
                                page={this.state.page}
                                total={this.state.total}
                            />
                        </>
                    )}
                />
            </>
        );
    }

    private async search() {
        const url = `/api/insurerServiceDeclarationRules?insurerId=${this.props.insurerId}&includeItemAuthorizations=canUpdate,canDelete&includeListAuthorizations=canAdd&page=${this.state.page}`;
        await this.setState({ isSearching: true });
        const result = await fetchJson(url, 'GET');
        await this.setState({
            insurerServiceDeclarationRules: result.json.items,
            isSearching: false,
            authorization: result.json.authorization,
        });
    }

    @bind
    private async handleNavigation(page: number) {
        await this.setState({ page: page });
        await this.search();
    }

    private async remove(id: string) {
        const url = `/api/insurerServiceDeclarationRules/${id}`;
        await this.setState({ isSearching: true, errorCode: undefined });
        const result = await fetchJson(url, 'DELETE', {}, [
            ValidationErrorCode.INSURER_SERVICE_HAS_LINKED_PRICES,
            ValidationErrorCode.INSURER_SERVICE_HAS_LINKED_DECLARATION_ACTIVITY_PERIODS,
        ]);

        if (result.errorCode) {
            await this.setState({ errorCode: result.errorCode });
        }

        await this.search();
    }
}
